import { ReactNode, forwardRef } from 'react';
import {
    RowData,
    Table,
    TableFeature,
    Updater,
    functionalUpdate,
    makeStateUpdater,
} from '@tanstack/react-table';
import { AnimatePresence, motion, useIsPresent } from 'framer-motion';
import { twMerge } from 'tailwind-merge';
import {
    RowReplacement,
    RowReplacementOptions,
    RowReplacementState,
    RowReplacementTableState,
} from 'types/react-table';

const RowReplacementFeature: TableFeature = {
    getInitialState: (state): RowReplacementTableState => {
        return {
            rowReplacement: [],
            ...state,
        };
    },

    getDefaultOptions: <TData extends RowData>(table: Table<TData>): RowReplacementOptions => {
        return {
            enableRowReplacement: false,
            onRowReplacementChange: makeStateUpdater('rowReplacement', table),
        } as RowReplacementOptions;
    },

    createTable: <TData extends RowData>(table: Table<TData>): void => {
        if (table.options?.meta?.renderRow) {
            throw new Error(
                'renderRow should not be defined when RowReplacementFeature is enabled',
            );
        }

        table.setRowReplacement = updater => {
            const safeUpdater: Updater<RowReplacementState> = old => functionalUpdate(updater, old);
            return table.options.onRowReplacementChange?.(safeUpdater);
        };

        table.addRowReplacement = value => {
            table.setRowReplacement((old: RowReplacement[]) => {
                if (value) {
                    // NOTE: remove any existing row replacement with the same rowId, then add the new row replacement
                    old = old.filter(rowReplacement => rowReplacement.rowId !== value.rowId);
                    return [...old, value];
                }

                return old;
            });
        };

        table.removeRowReplacement = value => {
            table.setRowReplacement((old: RowReplacement[]) => {
                if (value) {
                    return old.filter(rowReplacement => rowReplacement.rowId !== value);
                }

                return old;
            });
        };

        table.updateRowReplacement = value => {
            table.setRowReplacement((old: RowReplacement[]) => {
                if (value) {
                    return value;
                }
                return old;
            });
        };

        table._renderRowReplacementRows = (table, row, initialTableRow, children) => {
            const rowId = row.id;
            const tableState = table.getState();
            const isRowPending =
                table.options.enableRowPending && tableState.rowPending.includes(rowId);

            if (!table.options.enableRowReplacement) {
                return initialTableRow;
            } else {
                const rowReplacementMatch = tableState?.rowReplacement?.find(
                    r => r.rowId === rowId,
                );
                const colSpan = table.getAllColumns().length;

                return (
                    <AnimatePresence initial={false} mode="popLayout">
                        {rowReplacementMatch ? (
                            <ReplacementTableRowWithMotion
                                key={`${rowId}_replacement`}
                                rowReplacementMatch={rowReplacementMatch}
                                colSpan={colSpan}
                            />
                        ) : (
                            <OriginalTableRowWithMotion key={rowId} isRowPending={isRowPending}>
                                {children}
                            </OriginalTableRowWithMotion>
                        )}
                    </AnimatePresence>
                );
            }
        };

        table.setOptions({
            ...table.options,
            mergeOptions: (defaultOptions, options) => {
                return {
                    ...defaultOptions,
                    ...options,
                    meta: {
                        ...table.options.meta,
                        renderRow: table._renderRowReplacementRows,
                    },
                };
            },
        });
    },
};

const ReplacementTableRowWithMotion = forwardRef<
    HTMLTableRowElement,
    {
        colSpan: number;
        rowReplacementMatch: RowReplacement;
    }
>(({ colSpan, rowReplacementMatch }, replacementRef) => {
    const isPresent = useIsPresent();
    const replacementMaxHeight = rowReplacementMatch?.maxHeight ?? 100;
    const duration = 0.3; // seconds;

    return (
        <motion.tr
            className={twMerge(
                `group h-auto min-h-0 ${
                    !isPresent ? 'flex w-full justify-center [&>td]:block [&>td]:flex-grow' : ''
                }`,
            )}
            ref={replacementRef}
            initial={{ opacity: 0, maxHeight: replacementMaxHeight }}
            animate={{ opacity: 1, maxHeight: replacementMaxHeight }}
            exit={{ opacity: 0, maxHeight: 0 }}
            transition={{ duration }}
        >
            <td
                colSpan={colSpan}
                className="border-t border-t-gray-100 bg-white px-4 text-sm first:pl-6 last:pr-6 group-first:border-none group-last:first:rounded-bl-md group-last:last:rounded-br-md"
            >
                {rowReplacementMatch.content}
            </td>
        </motion.tr>
    );
});

const OriginalTableRowWithMotion = forwardRef<
    HTMLTableRowElement,
    {
        isRowPending?: boolean;
        children: ReactNode;
    }
>(({ isRowPending, children }, originalRef) => {
    const isPresent = useIsPresent();
    const TABLE_ROW_MAX_HEIGHT = 1000; // NOTE: a large number to ensure the row is visible (for animating height down to smaller sizes)
    const duration = 0.5; // seconds;

    return (
        <motion.tr
            className={twMerge(
                `group h-auto min-h-0 ${
                    isRowPending
                        ? 'bg-gray-25 pointer-events-none select-none [&>td]:bg-transparent [&>td]:opacity-[0.31]'
                        : ''
                } ${!isPresent ? 'flex w-full justify-center [&>td]:block [&>td]:flex-grow' : ''}`,
            )}
            layout
            ref={originalRef}
            initial={{ opacity: 0, maxHeight: 0 }}
            animate={{ opacity: 1, maxHeight: TABLE_ROW_MAX_HEIGHT }}
            exit={{ opacity: 0, maxHeight: 0 }}
            transition={{ duration }}
        >
            {children}
        </motion.tr>
    );
});

export { RowReplacementFeature };
